<template>
  <v-dialog :value="open" max-width="800" persistent scrollable>
    <v-card>
      <v-card-title class="headline">
        {{ $t('producto.modals.transferirStock.title') }}
      </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12">
               <v-select
                 v-model="depositoDestinoId"
                 :items="depositosDestinoFiltrados"
                 item-text="nombre"
                 item-value="id"
                 :label="$t('producto.modals.transferirStock.depositoDestino')"
                 :error="hasError('depositoDestino')"
                 :error-messages="getError('depositoDestino')"
                 :loading="loadingDepositos"
                 :disabled="!todosProductosMismoDeposito || depositosDestinoFiltrados.length === 0"
                 :no-data-text="depositosDestinoFiltrados.length === 0 ? 'No hay depósitos disponibles' : 'Sin datos'"
                 required
                 prepend-icon="warehouse"
               ></v-select>
              <v-alert v-if="!todosProductosMismoDeposito" type="warning" dense text class="mt-2">
                {{ $t('producto.modals.transferirStock.warningDepositosDiferentes') }}
              </v-alert>
              <v-alert v-if="depositosDestinoFiltrados.length === 0 && !loadingDepositos" type="info" dense text class="mt-2">
                No hay depósitos destino disponibles. AsegÃºrese de tener al menos un depósito asignado ademÃ¡s del depósito origen.
              </v-alert>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <v-row>
            <v-col cols="12">
              <h3 class="mb-3">{{ $t('producto.modals.transferirStock.productosSeleccionados') }}</h3>
              <v-alert v-if="productosConCantidad.length === 0" type="info" dense text class="mb-3">
                No hay productos seleccionados para transferir.
              </v-alert>
              <v-simple-table v-else>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">{{ $t('producto.labels.marca') }}</th>
                      <th class="text-left">{{ $t('producto.labels.modelo') }}</th>
                      <th class="text-left">{{ $t('producto.modals.transferirStock.stockDisponible') }}</th>
                      <th class="text-left">{{ $t('producto.modals.transferirStock.cantidad') }}</th>
                      <th class="text-center" style="width: 80px;">{{ $t('common.actions') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(producto, index) in productosConCantidad" :key="producto.id || index">
                      <td>{{ (producto.marca && producto.marca.nombre) || '-' }}</td>
                      <td>{{ (producto.modelo && producto.modelo.nombre) || '-' }}</td>
                      <td>
                        <v-chip :color="getStockColor(producto.stock)" dark small>
                          {{ producto.stock || 0 }}
                        </v-chip>
                      </td>
                      <td>
                        <v-text-field
                          v-model.number="producto.cantidadTransferir"
                          type="number"
                          :min="1"
                          :max="producto.stock"
                          :rules="[
                            v => !!v || $t('producto.modals.transferirStock.errorCantidadRequerida'),
                            v => v > 0 || $t('producto.modals.transferirStock.errorCantidadMinima'),
                            v => v <= producto.stock || $t('producto.modals.transferirStock.errorCantidadExcede')
                          ]"
                          dense
                          outlined
                          hide-details="auto"
                          @input="validateCantidad(producto)"
                        ></v-text-field>
                      </td>
                      <td class="text-center">
                        <v-btn
                          icon
                          small
                          color="error"
                          @click="removeProducto(index)"
                          :disabled="loading"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">
          {{ $t('common.cancel') }}
        </v-btn>
        <v-btn 
          color="primary" 
          @click="confirm" 
          :disabled="!valid || !depositoDestinoId || loading"
          :loading="loading"
        >
          {{ $t('common.confirm') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ProductoProvider from "../../../providers/ProductoProvider";
import DepositoProvider from "../../../../deposito/providers/DepositoProvider";
import UserConfigurationProvider from "../../../../user-configuration/providers/UserConfigurationProvider";

export default {
  name: "ProductoTransferirStockModal",
  props: {
    open: { type: Boolean, default: false },
    selectedProductos: { type: Array, default: () => [] },
  },
  data() {
    return {
      valid: false,
      loading: false,
      loadingDepositos: false,
      depositoDestinoId: null,
      depositosDestino: [],
      depositosDestinoFiltrados: [],
      productosConCantidad: [],
      errors: {},
    };
  },
  computed: {
    todosProductosMismoDeposito() {
      if (this.productosConCantidad.length === 0) {
        return true;
      }

      const primerProducto = this.productosConCantidad[0];
      if (!primerProducto) {
        return false;
      }
      const primerDeposito = (primerProducto.deposito && primerProducto.deposito.id) || primerProducto.deposito;
      if (!primerDeposito) {
        return false;
      }

      return this.productosConCantidad.every(producto => {
        const depositoId = (producto.deposito && producto.deposito.id) || producto.deposito;
        return String(depositoId) === String(primerDeposito);
      });
    },
  },
  watch: {
    open(newVal) {
      if (newVal) {
        // Cuando se abre el modal, inicializar con los productos actualizados
        this.initialize();
      }
    },
  },
  mounted() {
    // Si el modal ya está abierto al montarse, inicializar
    if (this.open) {
      this.initialize();
    }
  },
  methods: {
    async initialize() {
      // Primero inicializar productos para que tengan el campo deposito
      await this.initializeProductos();
      // Luego cargar depósitos (que también filtrará)
      await this.loadDepositos();
    },
     async loadDepositos() {
       this.loadingDepositos = true;
       try {
         // Obtener configuración del usuario para filtrar depósitos asignados
         const userConfigResponse = await UserConfigurationProvider.getUserConfiguration(true);
         const userConfig = userConfigResponse && userConfigResponse.data && userConfigResponse.data.userConfigurationGet ? userConfigResponse.data.userConfigurationGet : null;
         
         if (!userConfig) {
           this.depositosDestino = [];
           this.depositosDestinoFiltrados = [];
           this.loadingDepositos = false;
           return;
         }
         
         let depositosAsignados = [];
         
         // Si tenemos depósitos poblados directamente, usarlos
         if (userConfig.depositos && userConfig.depositos.length > 0) {
           depositosAsignados = userConfig.depositos.map(deposito => ({
             id: String(deposito.id),
             nombre: deposito.nombre,
           }));
         } else {
           // Si no, obtener todos los depósitos y filtrar por depositoIds
           const depositosResponse = await DepositoProvider.fetchDepositos();
           const todosDepositos = (depositosResponse && depositosResponse.data && depositosResponse.data.depositoFetch) ? depositosResponse.data.depositoFetch : [];
           
           // Filtrar solo los depósitos asignados al usuario
           if (userConfig.depositoIds && userConfig.depositoIds.length > 0) {
             const depositoIdsStr = userConfig.depositoIds.map(id => String(id));
             depositosAsignados = todosDepositos
               .filter(deposito => depositoIdsStr.includes(String(deposito.id)))
               .map(deposito => ({
                 id: String(deposito.id),
                 nombre: deposito.nombre,
               }));
           } else if (userConfig.depositoIdSeleccionado) {
             // Fallback para compatibilidad
             const depositoIdStr = String(userConfig.depositoIdSeleccionado);
             depositosAsignados = todosDepositos
               .filter(deposito => String(deposito.id) === depositoIdStr)
               .map(deposito => ({
                 id: String(deposito.id),
                 nombre: deposito.nombre,
               }));
           }
         }
         
         this.$set(this, 'depositosDestino', depositosAsignados);
         // Filtrar depósitos destino excluyendo el depósito origen de los productos
         this.filtrarDepositosDestino();
       } catch (error) {
         console.error("Error loading depositos:", error);
         this.$set(this, 'depositosDestino', []);
         this.$set(this, 'depositosDestinoFiltrados', []);
       } finally {
         this.loadingDepositos = false;
       }
     },
     filtrarDepositosDestino() {
       if (!this.depositosDestino || this.depositosDestino.length === 0) {
         this.$set(this, 'depositosDestinoFiltrados', []);
         return;
       }
       
       if (this.productosConCantidad.length === 0) {
         // Si no hay productos seleccionados, mostrar todos los depósitos
         this.$set(this, 'depositosDestinoFiltrados', this.depositosDestino);
         return;
       }
       
       // Obtener depósitos únicos de origen de todos los productos
       const depositosOrigen = new Set();
       this.productosConCantidad.forEach(producto => {
         const depositoId = (producto.deposito && producto.deposito.id) || producto.deposito;
         if (depositoId) {
           depositosOrigen.add(String(depositoId));
         }
       });
       
       // Filtrar depósitos destino excluyendo los de origen
       const filtrados = this.depositosDestino.filter(deposito => {
         return !depositosOrigen.has(String(deposito.id));
       });
       
       this.$set(this, 'depositosDestinoFiltrados', filtrados);
     },
     async initializeProductos() {
       // Consultar los productos actualizados desde el backend
       try {
         const productosActualizados = [];
         for (const producto of this.selectedProductos) {
           const response = await ProductoProvider.findProducto(producto.id);
           if (response && response.data && response.data.productoFind) {
             productosActualizados.push(response.data.productoFind);
           }
         }
         
         // Inicializar productos con cantidad 0, usando los datos actualizados del backend
         this.productosConCantidad = productosActualizados.map(producto => {
           return {
             id: producto.id,
             marca: producto.marca,
             modelo: producto.modelo,
             tamanio: producto.tamanio,
             codigoBarras: producto.codigoBarras,
             codigoBarrasProveedor: producto.codigoBarrasProveedor,
             precio: producto.precio,
             precioCompra: producto.precioCompra,
             stock: producto.stock,
             stockAlerta: producto.stockAlerta,
             fechaVencimiento: producto.fechaVencimiento,
             deposito: producto.deposito,
             cantidadTransferir: 0,
           };
         });
       } catch (error) {
         console.error("Error loading productos actualizados:", error);
         // Fallback: usar los productos del prop si hay error
         this.productosConCantidad = this.selectedProductos.map(producto => {
           return {
             id: producto.id,
             marca: producto.marca,
             modelo: producto.modelo,
             tamanio: producto.tamanio,
             codigoBarras: producto.codigoBarras,
             codigoBarrasProveedor: producto.codigoBarrasProveedor,
             precio: producto.precio,
             precioCompra: producto.precioCompra,
             stock: producto.stock,
             stockAlerta: producto.stockAlerta,
             fechaVencimiento: producto.fechaVencimiento,
             deposito: producto.deposito,
             cantidadTransferir: 0,
           };
         });
       }
       
       // Re-filtrar depósitos destino después de actualizar productos (solo si ya hay depósitos cargados)
       if (this.depositosDestino && this.depositosDestino.length > 0) {
         this.filtrarDepositosDestino();
       }
     },
    validateCantidad(producto) {
      if (producto.cantidadTransferir > producto.stock) {
        producto.cantidadTransferir = producto.stock;
      }
      if (producto.cantidadTransferir < 0) {
        producto.cantidadTransferir = 0;
      }
    },
    removeProducto(index) {
      // Eliminar el producto del array
      this.productosConCantidad.splice(index, 1);
      // Re-filtrar los depósitos destino después de eliminar un producto
      this.filtrarDepositosDestino();
      // Si no quedan productos, cerrar el modal automáticamente
      if (this.productosConCantidad.length === 0) {
        this.$emit('info', this.$t('producto.modals.transferirStock.todosProductosEliminados'));
        this.close();
      }
    },
    getStockColor(stock) {
      if (!stock || stock === 0) return "red";
      return "green";
    },
    hasError(field) {
      return !!this.errors[field];
    },
    getError(field) {
      return this.errors[field] || "";
    },
    async confirm() {
      if (!this.$refs.form.validate()) {
        return;
      }

      if (!this.depositoDestinoId) {
        this.errors.depositoDestino = this.$t('producto.modals.transferirStock.errorDepositoRequerido');
        return;
      }

      // Validar que todas las cantidades sean válidas
      const transferencias = [];
      let hasErrors = false;

      for (const producto of this.productosConCantidad) {
        if (!producto.cantidadTransferir || producto.cantidadTransferir <= 0) {
          hasErrors = true;
          continue;
        }

        if (producto.cantidadTransferir > producto.stock) {
          hasErrors = true;
          continue;
        }

        // Obtener deposito origen del producto
        const depositoOrigenId = (producto.deposito && producto.deposito.id) || producto.deposito;
        
        if (!depositoOrigenId) {
          hasErrors = true;
          continue;
        }

        // No permitir transferir al mismo depósito
        if (String(depositoOrigenId) === String(this.depositoDestinoId)) {
          hasErrors = true;
          continue;
        }

        transferencias.push({
          productoId: producto.id,
          depositoOrigenId: depositoOrigenId,
          depositoDestinoId: this.depositoDestinoId,
          cantidad: producto.cantidadTransferir,
        });
      }

      if (hasErrors || transferencias.length === 0) {
        this.$emit('error', this.$t('producto.modals.transferirStock.errorValidacion'));
        return;
      }

      this.loading = true;
      try {
        const response = await ProductoProvider.transferirStock(transferencias);
        
        if (response && response.data && response.data.productoTransferirStock && response.data.productoTransferirStock.success) {
          this.$emit('success', response.data.productoTransferirStock.mensaje);
          this.close();
        } else {
          const mensaje = (response && response.data && response.data.productoTransferirStock && response.data.productoTransferirStock.mensaje) || this.$t('producto.modals.transferirStock.errorTransferencia');
          this.$emit('error', mensaje);
        }
      } catch (error) {
        const errorMessage = (error && error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].message) || 
                           (error && error.message) || 
                           this.$t('producto.modals.transferirStock.errorTransferencia');
        this.$emit('error', errorMessage);
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.reset();
      this.$emit('close');
    },
    reset() {
      this.depositoDestinoId = null;
      this.productosConCantidad = [];
      this.depositosDestino = [];
      this.depositosDestinoFiltrados = [];
      this.errors = {};
      this.valid = false;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>

<style scoped></style>

